import { useState, useEffect, useRef } from 'react';
import { useApp } from '@context/AppContext';
import { useChallenge } from './useChallenge';
import { buildLeaderboard } from '@utils/scoreCalculator';

/**
 * Animated leaderboard reveal — lowest to highest, 1.5s per position.
 * Shows tie/draw-off prompt if applicable.
 */
export default function ChallengeResults() {
  const { state } = useApp();
  const { game, user } = state;
  const { startDrawOff, playAgain, endGame, votingMode } = useChallenge();
  const [revealIndex, setRevealIndex] = useState(-1);
  const timerRef = useRef(null);

  const leaderboard = buildLeaderboard(game.scores || {}, game.submissions || {});
  const hasTie = game.drawoffEligible && game.drawoffEligible.length > 1;

  // Animate reveal: one position every 1.5 seconds
  useEffect(() => {
    if (leaderboard.length === 0) return;
    let idx = 0;
    const reveal = () => {
      setRevealIndex(idx);
      idx++;
      if (idx < leaderboard.length) {
        timerRef.current = setTimeout(reveal, 1500);
      }
    };
    timerRef.current = setTimeout(reveal, 500);
    return () => clearTimeout(timerRef.current);
  }, [leaderboard.length]);

  const allRevealed = revealIndex >= leaderboard.length - 1;

  // Medal emoji for top 3
  const getMedal = (position, total) => {
    const rank = total - position;
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return '';
  };

  const maxScore = votingMode === 'stars' ? 10 : 10;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 text-center">
          <h2 className="text-xl font-bold text-charcoal font-display">
            {hasTie && allRevealed ? "It's a Tie!" : '🏆 Results'}
          </h2>
          {game.challenge && (
            <p className="text-xs text-slate-400 font-display mt-1">{game.challenge.text}</p>
          )}
        </div>

        {/* Leaderboard */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {leaderboard.map((entry, idx) => {
            const isRevealed = idx <= revealIndex;
            const medal = getMedal(idx, leaderboard.length);
            const isWinner = idx === leaderboard.length - 1 && !hasTie;

            return (
              <div
                key={entry.userId}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all duration-500 ${
                  isRevealed
                    ? isWinner
                      ? 'border-amber/40 bg-amber/5 scale-[1.02]'
                      : 'border-slate-100 bg-white'
                    : 'border-transparent bg-slate-50 opacity-0 translate-y-4'
                }`}
                style={{
                  transitionDelay: isRevealed ? '0ms' : '300ms',
                }}
              >
                {/* Thumbnail */}
                {entry.imageData && (
                  <img
                    src={entry.imageData}
                    alt={entry.nickname}
                    className="w-12 h-12 rounded-lg object-cover shrink-0 bg-canvas"
                  />
                )}

                {/* Name + medal */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-charcoal font-display truncate">
                    {medal && <span className="mr-1">{medal}</span>}
                    {entry.nickname}
                    {entry.userId === user.id && (
                      <span className="text-[10px] text-amber font-mono ml-1.5">you</span>
                    )}
                  </p>
                </div>

                {/* Score */}
                <div className="text-right shrink-0">
                  <span className={`text-lg font-bold font-mono ${
                    isWinner ? 'text-amber' : 'text-charcoal'
                  }`}>
                    {entry.score}
                  </span>
                  <span className="text-xs text-slate-300 font-mono">/{maxScore}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions (show after full reveal) */}
        {allRevealed && (
          <div className="px-5 py-4 border-t border-slate-100 space-y-2">
            {hasTie && user.isHost && (
              <button
                onClick={() => startDrawOff()}
                className="w-full px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold font-display hover:bg-red-600 transition-colors"
              >
                Draw-Off!
              </button>
            )}
            {user.isHost && (
              <div className="flex gap-3">
                <button
                  onClick={playAgain}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors"
                >
                  Play Again
                </button>
                <button
                  onClick={endGame}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-display text-slate-500 hover:bg-slate-50 transition-colors"
                >
                  End Game
                </button>
              </div>
            )}
            {!user.isHost && (
              <p className="text-sm text-slate-400 font-display text-center">
                Waiting for host...
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
